import { DateTime } from 'luxon'
import { headerMap, sectionNameMap } from '../constants'
import { toTitleCase } from '../../../utils/string'
import { Config } from '../../../shared/types'

type Options = { timeZone: string }

export function formatDate(date: string, options?: Options) {
  if (!date) {
    return null
  }

  const dt = DateTime.fromISO(date)

  if (dt.isValid) {
    if (options?.timeZone) {
      return dt.toLocaleString({ timeZone: options.timeZone })
    }

    return dt.toLocaleString()
  }

  return date
}

export function formatReportPeriod(
  value: string | { startDate: string; endDate: string; text?: string },
) {
  if (!value) {
    return null
  }

  if (typeof value === 'string') {
    return value
  }

  if (value.text) {
    return value.text
  }

  return [value.startDate, value.endDate]
    .filter(Boolean)
    .map((date) => formatDate(date))
    .join(' to ')
}

//capitalize ar, ap, cc
function capitalizeAbbreviations(string: string): string {
  return string
    .replace(/^A[rp]\s/, (match) => match.toUpperCase())
    .replace(/\sA[rp]\s/g, (match) => match.toUpperCase())
    .replace(/^Cc\s/g, (match) => match.toUpperCase())
    .replace(/\sCc\s/g, (match) => match.toUpperCase())
    .replace(/(Id)(s)?/g, (match, p1, p2) => (p2 ? p1.toUpperCase().concat(p2) : p1.toUpperCase()))
}

export function formatHeader(header: string): string {
  if (headerMap[header]) {
    return headerMap[header]
  }

  return capitalizeAbbreviations(toTitleCase(header))
}

export function formatSectionName(sectionName: string): string {
  if (sectionNameMap[sectionName]) {
    return sectionNameMap[sectionName]
  }

  return capitalizeAbbreviations(toTitleCase(sectionName))
}

const configFormatters: Record<string, Function> = {
  percentVariance: (val: number) => val / 100,
}

export function formatConfig(config: Config) {
  const formattedConfig: Config = { ...config }

  Object.keys(config).forEach((key) => {
    const formatter = configFormatters[key]
    const val = config[key]

    if (formatter && val) {
      formattedConfig[key] = formatter(val)
    }
  })

  return formattedConfig
}
